/**
 * Helpers to compute listing stats on the checked rows instead of the active
 * filters. The API reads ``selected_ids`` on ``/stats/`` list endpoints and
 * then ignores list filters (visibility scoping still applies server-side).
 */

export const SELECTED_IDS_PARAM = "selected_ids";

/**
 * @callback SelectionIdGetter
 * @param {Record<string, unknown>} item
 * @returns {number|string|null|undefined}
 */

/** @type {SelectionIdGetter} */
const defaultGetId = (item) => item?.id;

/**
 * Persisted ids of the selected rows (virtual/unsaved rows are skipped).
 * @param {Array<Record<string, unknown>>|null|undefined} items
 * @param {SelectionIdGetter} [getId]
 * @returns {number[]}
 */
export function getSelectedIds(items, getId = defaultGetId) {
    if (!Array.isArray(items)) {
        return [];
    }
    const ids = new Set();
    for (const item of items) {
        const id = Number(getId(item));
        if (Number.isInteger(id) && id > 0) {
            ids.add(id);
        }
    }
    return [...ids];
}

/**
 * Stable key of a selection, used to trigger a stats refresh only when the
 * set of selected ids changes (not when rows are re-fetched).
 * @param {Array<Record<string, unknown>>|null|undefined} items
 * @param {SelectionIdGetter} [getId]
 * @returns {string}
 */
export function selectedIdsKey(items, getId = defaultGetId) {
    return getSelectedIds(items, getId)
        .sort((a, b) => a - b)
        .join(",");
}

/**
 * Add the ``selected_ids`` param to stats request params when rows are checked.
 * @param {Record<string, unknown>} params
 * @param {Array<Record<string, unknown>>|null|undefined} items
 * @param {SelectionIdGetter} [getId]
 * @returns {Record<string, unknown>}
 */
export function withSelectedIds(params, items, getId = defaultGetId) {
    const ids = getSelectedIds(items, getId);
    if (!ids.length) {
        return params;
    }
    return { ...params, [SELECTED_IDS_PARAM]: ids.join(",") };
}
